import React, { useEffect, useState } from "react";
import { Link2, Copy, Ban } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";

const linkFor = (token) => `${window.location.origin}/?invite=${token}`;

const copy = (token) =>
  navigator.clipboard.writeText(linkFor(token)).then(() => toast.success("Invite link copied"), () => toast.error("Copy failed — select the link and copy it"));

function InviteRow({ inv, onRevoke }) {
  const dead = inv.revoked || inv.expired;
  return (
    <div className={`py-2 border-b border-border last:border-0 space-y-1 ${dead ? "opacity-50" : ""}`} data-testid={`invite-row-${inv.token}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="font-medium text-sm">{inv.workgroup_names.join(", ")}</span>
        <Badge variant="outline">{inv.uses} joined</Badge>
        {inv.revoked ? <Badge variant="outline" className="text-rose-300">revoked</Badge>
          : inv.expired ? <Badge variant="outline" className="text-rose-300">expired</Badge>
          : <span className="text-muted-foreground">expires {new Date(inv.expires_at).toLocaleDateString()}</span>}
        {!dead && (
          <span className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copy(inv.token)} data-testid={`invite-copy-${inv.token}`}><Copy className="w-3 h-3 mr-1" /> Copy link</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-300" onClick={() => onRevoke(inv.token)} data-testid={`invite-revoke-${inv.token}`}><Ban className="w-3 h-3 mr-1" /> Revoke</Button>
          </span>
        )}
      </div>
      {!dead && <code className="block text-[11px] text-muted-foreground break-all select-all" data-testid={`invite-url-${inv.token}`}>{linkFor(inv.token)}</code>}
    </div>
  );
}

/** Admin: reusable 7-day invite links; anyone with the link can sign up as a playtester. */
export function InviteLinks({ groups, onChanged }) {
  const [invites, setInvites] = useState([]);
  const [picked, setPicked] = useState([]);
  const load = () => api.listInvites().then(setInvites).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      const inv = await api.createInvite(picked);
      setPicked([]);
      await load();
      copy(inv.token);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const revoke = async (token) => {
    await api.revokeInvite(token).catch((e) => toast.error(formatError(e)));
    load();
    onChanged?.();
  };
  return (
    <Card>
      <CardContent className="p-4 space-y-3" data-testid="invite-links">
        <h3 className="font-semibold flex items-center gap-2"><Link2 className="w-4 h-4 text-cyan-400" /> Invite links</h3>
        <p className="text-xs text-muted-foreground">Anyone with the link can create their own playtester account (name, email, password) for 7 days. Revoke a link to stop new sign-ups.</p>
        <div className="flex flex-wrap items-center gap-3">
          {groups.map((g) => (
            <label key={g.id} className="flex items-center gap-1.5 text-xs">
              <Checkbox checked={picked.includes(g.id)} onCheckedChange={(v) => setPicked(v ? [...picked, g.id] : picked.filter((x) => x !== g.id))} data-testid={`invite-link-group-${g.id}`} />
              {g.name}
            </label>
          ))}
          <Button size="sm" onClick={create} disabled={!picked.length} data-testid="invite-link-create-btn">Create invite link</Button>
        </div>
        {invites.map((inv) => <InviteRow key={inv.token} inv={inv} onRevoke={revoke} />)}
      </CardContent>
    </Card>
  );
}
